import { defineStore } from 'pinia';
import { onUnmounted,ref } from 'vue';

import { fetchClientID } from '@/api/clientID';
import i18n from '@/i18n';
import router from '@/router';
import { useCartStore } from '@/stores/cart';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { useDoorsaleStore } from '@/stores/doorsaleStore';
import { useSeatmapStore } from '@/stores/seatmapStore';
import { useSsoStore } from '@/stores/sso';
import { useToastStore } from '@/stores/toastStore';
import type { PlaceOrder } from '@/types';
import { createWebsocketMessage } from '@/utils/createWebsocketMessage';

export const useWebsocketStore = defineStore('websocket', () => {
  const websocket = ref<WebSocket | undefined>(undefined);
  const messages = ref<Array<{ rtoken: string | undefined; id: number; data: object; action: string; clientid: string | undefined }>>([]); 
  const desiredState = ref('disconnected');
  const state = ref('disconnected');
  const rtoken = ref<string | undefined>(undefined);
  const id = ref<number>(0);
  const url = import.meta.env.DEV
    ? "ws://" + location.host + "/of2/ws/ticketsale"
    : "wss://" + location.hostname + "/of2/ws/ticketsale";
  const pingInterval = ref<number | undefined>(undefined);
  const lastPong = ref<number>(Date.now());
  const lastActive = ref<number | undefined>(undefined);
  const websocketTime = ref<number>(Date.now());
  const clientTime = ref<number>(Date.now());
  const clientID = ref<string | undefined>(undefined);
  const pongCount = ref<number>(0);

  function connect() {
    websocket.value = new WebSocket(url);

    websocket.value.onopen = () => {
      stateChanged();
    };

    websocket.value.onmessage = (event) => {
      handleMessage(event);
    };

    websocket.value.onclose = () => {
      stateChanged();
    };

    websocket.value.onerror = () => {
      stateChanged();
    };
    state.value = 'connecting';
  }

  function handleMessage(event: MessageEvent) {
    const message = JSON.parse(event.data);
    console.log("----- handleMessage -----");
    console.log("Received WebSocket message:", JSON.parse(JSON.stringify(message))); // make a copy to avoid the data changing in the logs
    console.log("Current WebSocket state:", state.value);
    if(state.value === 'authenticating'){
      if(message.action === 'rtoken'){
        rtoken.value = message.rtoken;
        saveRtoken();
        state.value = 'connected';
        // The server needs the sso login status of this connection to find member prices
        useSsoStore().syncWebsocket();
        //Send waiting message
        console.log("Sending waiting messages, count: " + messages.value.length);
        console.log(messages.value);
        while (messages.value.length > 0) {
          const msg = messages.value.shift();
          console.log(msg);
          if (msg) {
            msg.rtoken = rtoken.value;
            if (msg.clientid === undefined) {
              msg.clientid = clientID.value;
            }
            if(websocket.value) {
              websocket.value.send(JSON.stringify(msg));
            }
          }
        }
      }
      else {
        console.error("Bad action: " + message.action);
        if(message.action == 'badclientid'){
          getClientID(true);
        }
      }
    }
    else if(state.value == 'connected'){
      // Handle messages for the connected state
      if(message.action == 'pong'){
        syncTime(message);
        lastPong.value = getRealTime();
        pongCount.value += 1;
        if(pongCount.value > 5){
          //Practically anywhere not in checkout flow should close the connection if cart is empty
          // Never close during an active doorsale session — the user may be navigating
          // between steps and the cart can appear momentarily empty between basket updates.
          // Closing here wipes the rtoken from storage, causing a new empty rtoken on
          // the next open() call from any doorsale view, permanently losing all reservations.
          const doorsaleStore = useDoorsaleStore();
          if(!doorsaleStore.isActive && !router.currentRoute.value.path.includes("checkout")){
            // Check if basket is empty, if so close connection
            const cartStore = useCartStore();
            if(cartStore.isCartEmpty()){
              pongCount.value = 0;
              close();
            }
          }
        }
      }
      else if (message.action == 'event') {
        const checkoutStore = useCheckoutStore();
        console.log("push event", message.data);
        
        checkoutStore.setCheckoutData(message.data);
      }
      else if (message.action == 'eventstate') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.setCheckoutEventState(message.data);
      }
      else if (message.action == 'reservetickets') {
        const cartStore = useCartStore();
        console.log("reservetickets", message.data);

        cartStore.updateReservedTickets(message.data);
      }
      else if (message.action == 'startpurchase') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.setStartPurchaseData(message.data);
        const cartStore = useCartStore();
        cartStore.updateCustomFieldsStartPurchase(message.data);
        // console.log("Start purchase data received:", message.data);
      }
      else if (message.action == 'orderdata') {
        const checkoutStore = useCheckoutStore();
        const { t } = i18n.global;
        if((message.data.errors && Object.keys(message.data.errors).length > 0) || (message.data.custom_field_errors && Object.keys(message.data.custom_field_errors).length > 0)){
          checkoutStore.isPlacingOrder = false;
          const mappedErrors = (message.data.errors as string[]).map((code: string) =>
            t(`order_errors.${code}`, t('order_errors.unknown'))
          );
          if(message.data.custom_field_errors?.length){
            checkoutStore.customFieldErrors = message.data.custom_field_errors;
            mappedErrors.push(t('order_errors.invalid_custom_fields'));
          }
          checkoutStore.orderErrors = mappedErrors;
        }
        else if(message.data.ok == 0) {
          checkoutStore.isPlacingOrder = false;
          checkoutStore.orderErrors = [t('order_errors.unknown')];
        }
        else {
          // Store IVP availability flags from orderdata response
          const ivpAvailable = !!message.data.valid_ean_payment;
          const hasDankortDiscount = checkoutStore.startPurchaseData.dankort_discount && checkoutStore.startPurchaseData.dankort_discount > 0;

          checkoutStore.ivpAvailable = ivpAvailable;
          checkoutStore.ivpEmailRequired = !!message.data.ivp_email_check;
          checkoutStore.ivpEanRequired = !!message.data.ivp_ean_check;

          // In doorsale mode there is no payment selection UI — always place the order directly
          const doorsaleStore = useDoorsaleStore();
          const cartStore = useCartStore();
          if (doorsaleStore.isActive) {
            doPlaceOrder('creditcard');
          // If giftcards cover the full total, skip payment entirely
          } else if (cartStore.cartTotals.total <= 0) {
            doPlaceOrder('NONE');
          } else if (ivpAvailable || hasDankortDiscount) {
            // Show payment method choice modal
            checkoutStore.isPlacingOrder = false;
            checkoutStore.paymentModal = true;
          } else {
            doPlaceOrder('creditcard');
          }
        }
      }
      else if (message.action == 'placeorder_doorsale') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.isPlacingOrder = false;
        checkoutStore.placeOrderPaymentVars = message.data.payment_vars;
        if(message.data.error){
          alert("Error placing order: " + message.data.error);
        }
        else if(message.data.ok == 1){
          router.push({ name: 'doorsale-receipt' });
        }
        else {
          alert("Unknown error placing order.");
        }
      }
      else if (message.action == 'placeorder') {
        const checkoutStore = useCheckoutStore();
        checkoutStore.placeOrderPaymentVars = message.data.payment_vars;
        if(message.data.error){
          alert("Error placing order: " + message.data.error);
        }
        else if(message.data.ok == 1 && message.data.proceed == 'gotoreceipt'){
          // Mark receipt mode immediately so incoming empty basket messages
          // don't wipe cart items before the receipt data arrives
          const cartStore = useCartStore();
          cartStore.receiptCart = true;
          router.push('/receipt?rtoken=' + (rtoken.value ?? ''));
        }
        else if( message.data.ok == 1 && checkoutStore.startPurchaseData.psp == 'quickpay10'){
          checkoutStore.startQuickPay10Payment();
        }
        else if( message.data.ok == 1 && checkoutStore.startPurchaseData.psp == 'epay'){
          checkoutStore.startEpayPayment();
        }
        else {
          alert("Unknown error placing order.");
        }
      }
      else if (message.action == 'basket') {
        const cartStore = useCartStore();
        cartStore.setSimpleBasketData(message.data);
      }
      else if (message.action == 'receipt') {
        const cartStore = useCartStore();
        cartStore.setReceiptOrder(message.data);
      }
      else if (message.action == 'confirmpayment') {
        const cartStore = useCartStore();
        cartStore.setReceiptConfirmPayment(message.data);
      }
      else if (message.action == 'pdfready') {
        const cartStore = useCartStore();
        cartStore.pdfReady = true;
      }
      else if (message.action == 'vouchercode'){
        const checkoutStore = useCheckoutStore();
        const cartStore = useCartStore();

        checkoutStore.setVouchercodeData(message.data);
        if(message.data.ok){
          cartStore.resendReservedTickets();
        }
        // Handle voucher code response tomorrow
      }
      else if (message.action == 'giftcard'){
        const cartStore = useCartStore();
        const toastStore = useToastStore();
        if(message.data.ok == 1 || message.data.ok === true){
          cartStore.addGiftcard({
            id: message.data.id,
            code: message.data.code,
            balance: message.data.balance,
            original_balance: message.data.balance,
            vat_percent: message.data.vat_percent,
            currency: message.data.currency,
          });
          toastStore.showToast(i18n.global.t('checkout.giftcard_added'));
        } else {
          toastStore.showToast(i18n.global.t('checkout.giftcard_not_found'));
        }
      }
      else if (message.action == 'unpaidorder'){
        const cartStore = useCartStore();
        cartStore.setUnpaidOrder(message.data);
      }
      else if (message.action == 'orderflowchanged'){
        close();
        const cartStore = useCartStore();
        const checkoutStore = useCheckoutStore();
        checkoutStore.clearCheckoutData();
        cartStore.resetCart();
        window.location.href = '/';
        window.location.reload();
      }
      else if (message.action == 'endconnection') {
        close();
      }
      else if (message.action == 'receiptnotfound') {
        // Server has no order for this rtoken (e.g. user navigated back to receipt
        // after the session expired). Signal via the cart store so the receipt view
        // can decide how to respond without the WS store owning routing logic.
        const cartStore = useCartStore();
        cartStore.receiptError = 'notfound';
      }
      else if (message.action == 'eventseatmaps') {
        const seatmapStore = useSeatmapStore();
        seatmapStore.handleEventseatmapsResponse(message.data);
      }
      else if (message.action == 'reserveseats') {
        const seatmapStore = useSeatmapStore();
        seatmapStore.handleReserveSeatsResponse(message.data);
      }
      else if (message.action == 'validatepricegroups') {
        const seatmapStore = useSeatmapStore();
        seatmapStore.handleValidatePriceGroupsResponse(message.data);
      }
      else if (message.action == 'checkseats') {
        // Server detected that seat reservations don't match the basket.
        // Redirect the user to seat selection for the mismatched event.
        const checkoutStore = useCheckoutStore();
        const toastStore = useToastStore();
        const eventId = message.data.event;
        const eventInfo = message.data.event_info;
        checkoutStore.setCheckoutID(eventId);
        send('subscribeevent', { id: eventId }, undefined);
        if (eventInfo) {
          toastStore.showToast(eventInfo);
        }
        const isDoorsale = router.currentRoute.value.path.startsWith('/doorsale');
        if (isDoorsale) {
          router.push({ name: 'doorsale-seats', params: { eventId } });
        } else {
          router.push('/checkout/seats');
        }
      }
      else {
        console.error("Bad action: " + message.action);
      }
    }
    console.log("Current client id:", clientID.value);
    // else {
    //   console.log("Received WebSocket message in unknown state:", message);
    // }

  }

  function stateChanged() {
    const readyState = getWebsocketReadyState();
    if (readyState === WebSocket.OPEN) {
      state.value = 'open';
    } 
    else if (readyState === WebSocket.CLOSING) {
      state.value = 'closing';
    }
    else if (readyState === WebSocket.CLOSED) {
      state.value = 'closed';
      websocket.value = undefined;
      // Do NOT wipe rtoken/clientID here. The rtoken must survive close()+open() cycles
      // that happen during doorsale navigation — wiping it causes a new empty server
      // session on reconnect, losing all basket reservations. Callers that genuinely
      // need a fresh session (endconnection, orderflowchanged, handleInactivity) either
      // do a full localStorage.clear() or the server issues a new rtoken automatically.
    }
    checkWebsocketState();
    if(desiredState.value == 'connected' && state.value == 'closed'){
      setTimeout(() => {
        state.value = 'disconnected';
        checkWebsocketState();
      }, 10000);
    }
    else if (desiredState.value == 'disconnected' && state.value == 'closed'){
      state.value = 'disconnected';
      checkWebsocketState();
    }
  }

  function doPlaceOrder(cardtype: string) {
    const checkoutStore = useCheckoutStore();
    const doorsaleStore = useDoorsaleStore();
    const cartStore = useCartStore();
    const PlaceOrderData: PlaceOrder = checkoutStore.orderData as PlaceOrder;
    PlaceOrderData.cardtype = cardtype;
    PlaceOrderData.language = checkoutStore.getCheckoutLanguage();
    PlaceOrderData.giftcards = cartStore.getGiftcardInfo();
    if (doorsaleStore.isActive) {
      PlaceOrderData.session = doorsaleStore.sessionId ?? undefined;
      send('placeorder_doorsale', PlaceOrderData, undefined);
    } else {
      PlaceOrderData.is_doorsale = false;
      PlaceOrderData.order_limit_customer_id = checkoutStore.getUrlParam('OL_Customer');
      send('placeorder', PlaceOrderData, undefined);
    }
  }

  function doPlaceOrderIVP() {
    const checkoutStore = useCheckoutStore();
    const cartStore = useCartStore();
    const PlaceOrderData: PlaceOrder = checkoutStore.orderData as PlaceOrder;
    PlaceOrderData.cardtype = 'NONE';
    PlaceOrderData.language = checkoutStore.getCheckoutLanguage();
    PlaceOrderData.is_doorsale = false;
    PlaceOrderData.ivp = 1;
    PlaceOrderData.ivp_name = checkoutStore.ivpFormData.ivp_name;
    PlaceOrderData.ivp_attperson = checkoutStore.ivpFormData.ivp_attperson;
    PlaceOrderData.ivp_address = checkoutStore.ivpFormData.ivp_address;
    PlaceOrderData.ivp_zip = checkoutStore.ivpFormData.ivp_zip;
    PlaceOrderData.ivp_city = checkoutStore.ivpFormData.ivp_city;
    PlaceOrderData.ivp_email = checkoutStore.ivpFormData.ivp_email;
    PlaceOrderData.ivp_ean = checkoutStore.ivpFormData.ivp_ean;
    PlaceOrderData.ivp_cvr = checkoutStore.ivpFormData.ivp_cvr;
    PlaceOrderData.ivp_reference = checkoutStore.ivpFormData.ivp_reference;
    PlaceOrderData.ivp_toa = checkoutStore.ivpFormData.ivp_toa;
    PlaceOrderData.giftcards = cartStore.getGiftcardInfo();
    PlaceOrderData.order_limit_customer_id = checkoutStore.getUrlParam('OL_Customer');
    send('placeorder', PlaceOrderData, undefined);
  }

  function checkWebsocketState () {
      console.log("Checking WebSocket state. Desired:", desiredState.value, "Actual:", state.value);
      if(desiredState.value == 'connected'){
        if(state.value == 'disconnected'){
          if(websocket.value === undefined){
            connect();
          }
        }
        else if (state.value == 'open') {
          // Transition to 'authenticating' synchronously before the async getClientID()
          // call. If checkWebsocketState() is called again while getClientID() is doing
          // a network fetch (which can take ~1 second), it will see 'authenticating' and
          // skip this branch, preventing a second rtoken being sent on the same socket.
          state.value = 'authenticating';
          id.value = 0;
          if(pingInterval.value !== undefined){
            clearInterval(pingInterval.value);
            pingInterval.value = undefined;
          }
          pingInterval.value = setInterval(() => {
            doPing();
          }, 30000);
          lastPong.value = getRealTime();
          getClientID().then(() => {
            if(clientID.value != undefined){
              restoreRtoken();
              console.log("Authenticating with client ID:", clientID.value, "and rtoken:", rtoken.value);
              send('rtoken', { }, clientID.value);
            }
            else {
              console.error("No client ID");
              // Only revert if the socket is still physically open and stateChanged()
              // hasn't already moved us to a different state (e.g. 'closed').
              if (state.value === 'authenticating' && getWebsocketReadyState() === WebSocket.OPEN) {
                state.value = 'open';
              }
            }
          });
        }
      }
      else if (desiredState.value == 'disconnected'){
        if(state.value == 'connecting' || state.value == 'open' || state.value == 'connected' || state.value == 'authenticating'){
          disconnect();
          state.value = 'closing';
        }
      }
  }

  function doPing() {
    if (websocket.value && state.value == 'connected') {
      if(getRealTime() - lastPong.value > 59000){
        disconnect();
        clearInterval(pingInterval.value);
        return;
      }
      else {
        const href = location.href;
        const cartStore = useCartStore();
        const doorsaleStore = useDoorsaleStore();
        const isBasketEmpty = cartStore.isCartEmpty();
        // An active doorsale session is never considered idle — the user may be navigating between steps and the basket can appear momentarily empty.
        if(lastActive.value == undefined || href.includes("checkout") || href.includes("payment") || !isBasketEmpty || doorsaleStore.isActive){
          lastActive.value = getRealTime();
        }
        else if (getRealTime() - lastActive.value > 899000) {
          close();
          clearInterval(pingInterval.value);
          return;
        }
      }
      send('ping', {}, undefined);
    }
    else if (state.value != 'open' && state.value != 'authenticating') {
      // Handle other states
    }
  }

  function syncTime(msg: { data: { servertime: string } }) {
    const dateTime = new Date();
    clientTime.value = dateTime.getTime();
    if (msg.data && msg.data.servertime) {
      websocketTime.value = parseInt(msg.data.servertime, 10) * 1000;
    }
    else {
      console.error("No server time in sync message");
      websocketTime.value = dateTime.getTime();
    }
  }

  function getRealTime(): number {
    const date = new Date();
    const dateTime = date.getTime();

    const delta = websocketTime.value - clientTime.value;

    const realTime = dateTime + delta;
    return realTime;
  }

  function getWebsocketReadyState(): number {
    return websocket.value?.readyState ?? WebSocket.CLOSED;
  }

  function send(action: string, data: object, clientIDParam: string | undefined) {
    // The ticket prices depend on the sso login status the server has for this connection (same as the old client:
    // the status is sent right before every event subscription). Doorsale does not use the visitor's sso login.
    if (action === 'subscribeevent' && !useDoorsaleStore().isActive) {
      const ssoStore = useSsoStore();
      if (ssoStore.isLoading) {
        ssoStore.whenReady().then(() => {
          ssoStore.sendLoginStatus();
          doSend(action, data, clientIDParam);
        });
        return;
      }
      ssoStore.sendLoginStatus();
    }
    doSend(action, data, clientIDParam);
  }

  function doSend(action: string, data: object, clientIDParam: string | undefined) {
    const effectiveClientID = clientIDParam ?? clientID.value;
    if (websocket.value && (state.value == 'connected' || (state.value == 'authenticating' && action == 'rtoken'))) {
      websocket.value.send(JSON.stringify(createWebsocketMessage(action, data, effectiveClientID)));
    }
    else {
      messages.value.push(createWebsocketMessage(action, data, effectiveClientID));
    }
    console.log("Sent WebSocket message:", createWebsocketMessage(action, data, effectiveClientID));
    console.log(action, data, effectiveClientID);
  }

  function isConnected(): boolean {
    return state.value === 'connected';
  }

  function close(){
    console.log("Closing WebSocket connection");
    desiredState.value = 'disconnected';
    checkWebsocketState();
  }

  // Wipe the rtoken from memory and storage so the next open() authenticates with no
  // rtoken, causing the server to issue a fresh rtoken(n) rather than resuming an
  // existing session. Call this only for a deliberate clean slate (e.g. inactivity
  // timeout reset) — NOT on reconnects where preserving the basket matters.
  function clearSession() {
    rtoken.value = undefined;
    localStorage.removeItem('rtoken');
    localStorage.removeItem('rtokenTimestamp');
    sessionStorage.removeItem('rtoken');
    sessionStorage.removeItem('rtokenTimestamp');
  }

  // Clear the cached clientID from memory and storage so the next getClientID() call
  // fetches a fresh one from the server. The new clientID inherits the permissions of
  // the active server session (e.g. has_doorsale_permission = 1 when the admin session
  // cookie is present). Called in two places:
  //   1. Inactivity reset — localStorage.clear() wipes storage but clientID.value in
  //      Pinia memory is not cleared by clearSession(), so this finishes the job.
  //   2. Doorsale router guard — ensures the clientID is fetched while the admin
  //      session cookie is active before the WS connection is opened.
  function clearClientID() {
    clientID.value = undefined;
    localStorage.removeItem('clientID');
    localStorage.removeItem('clientIDTimestamp');
    sessionStorage.removeItem('clientID');
    sessionStorage.removeItem('clientIDTimestamp');
  }

  function open(){
    desiredState.value = 'connected';
    checkWebsocketState();
  }

  function disconnect() {
    websocket.value?.close();
  }

  async function getClientID(reload: boolean = false) {
    if(clientID.value != undefined && !reload){
      return;
    }
    //Get client ID from storage or API
    const now = Date.now();
    let storedClientID = localStorage.getItem('clientID');
    let storedClientIDTimestamp = localStorage.getItem('clientIDTimestamp');
    //If we cannot get it from localstoage, get it from session storage
    if(!storedClientID){
      storedClientID = sessionStorage.getItem('clientID');
      storedClientIDTimestamp = sessionStorage.getItem('clientIDTimestamp');
    }
    if(storedClientID && storedClientIDTimestamp){
      const timestamp = parseInt(storedClientIDTimestamp, 10);
      //Check if client ID is older than 15 minutes
      if(now - timestamp < 15 * 60 * 1000){
        clientID.value = storedClientID;
        return;
      }
    }
    clientID.value = await fetchClientID();
  }

  function saveRtoken (rtokenValue?: string) {
    // save rtoken to local and session storage
    if(rtokenValue == undefined && rtoken.value != undefined){
      rtokenValue = rtoken.value;
    }
    else if(!rtokenValue && !rtoken.value){
      return;
    }
    const rtokenToStore = rtokenValue as string;
    localStorage.setItem('rtoken', rtokenToStore);
    localStorage.setItem('rtokenTimestamp', Date.now().toString());
    sessionStorage.setItem('rtoken', rtokenToStore);
    sessionStorage.setItem('rtokenTimestamp', Date.now().toString());
  }

  function restoreRtoken () {
    let storedRtoken = localStorage.getItem('rtoken');
    let storedRtokenTimestamp = localStorage.getItem('rtokenTimestamp');
    if(!storedRtoken || !storedRtokenTimestamp){
      storedRtoken = sessionStorage.getItem('rtoken');
      storedRtokenTimestamp = sessionStorage.getItem('rtokenTimestamp');
    }
    if(storedRtoken && storedRtokenTimestamp){
      const timestamp = parseInt(storedRtokenTimestamp, 10);
      //Check if rtoken is older than 15 minutes
      if(Date.now() - timestamp < 15 * 60 * 1000){
        rtoken.value = storedRtoken;
        return;
      }
    }
    else {
      return undefined;
    }
  }

  function restoreConnection(){
    //If we have a client ID, try to reconnect
    if(state.value == 'disconnected') {
      getClientID().then(() => {
        if(clientID.value != undefined){
          open();
        }
      });
    }
  }

  onUnmounted(() => {
    disconnect();
  });

  return {
    open,
    close,
    clearSession,
    clearClientID,
    send,
    isConnected,
    id,
    rtoken,
    saveRtoken,
    doPlaceOrder,
    doPlaceOrderIVP,
    restoreConnection,
  };
});